"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, AlertCircle, Clock, TrendingUp, Users, Target, Zap, FileText, Settings, Shield } from "lucide-react";

export default function ModalTwoColumnInfoExample() {
  const [open, setOpen] = React.useState(true);

  const onboardingProgress = [
    { label: "Company Information", value: "Complete", progress: 100, icon: Settings, color: "text-green-600" },
    { label: "Document Types", value: "7 selected", progress: 100, icon: FileText, color: "text-green-600" },
    { label: "Team Setup", value: "3 members", progress: 75, icon: Users, color: "text-blue-600" },
    { label: "Security Settings", value: "In Progress", progress: 50, icon: Shield, color: "text-orange-600" }
  ];

  const onboardingSteps = [
    { id: 1, title: "Company Context Completed", status: "completed", time: "2 hours ago", icon: CheckCircle },
    { id: 2, title: "Document Types Selected", status: "completed", time: "3 hours ago", icon: CheckCircle },
    { id: 3, title: "Team Members Added", status: "in-progress", time: "1 hour ago", icon: AlertCircle },
    { id: 4, title: "Security Configuration", status: "pending", time: "Not started", icon: Clock }
  ];

  const nextActions = [
    { title: "Complete Team Setup", description: "Add remaining team members and assign roles", icon: Users, variant: "default" as const },
    { title: "Configure Security", description: "Set up security policies and access controls", icon: Shield, variant: "secondary" as const },
    { title: "Review Settings", description: "Review all onboarding configurations", icon: Target, variant: "outline" as const }
  ];

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Onboarding Review</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Onboarding Review</h2>
              <p className="mt-2 text-muted-foreground">Review your onboarding progress and complete remaining setup steps</p>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <Badge variant="outline">Started: 2 hours ago</Badge>
                <Badge variant="secondary">75% Complete</Badge>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Progress & Steps */}
                <div className="space-y-6">
                  {/* Onboarding Progress */}
                  <Card className="bg-white/5 border-white/20">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Onboarding Progress
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {onboardingProgress.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <div key={index} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${item.color}`} />
                                <span className="text-sm font-medium">{item.label}</span>
                              </div>
                              <span className="text-sm font-bold">{item.value}</span>
                            </div>
                            <Progress value={item.progress} className="h-2 bg-white/10" />
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Completed Steps */}
                  <Card className="bg-white/5 border-white/20">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Completed Steps
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {onboardingSteps.map((step) => {
                        const Icon = step.icon;
                        return (
                          <div key={step.id} className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                            <Icon className="h-5 w-5 text-muted-foreground mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{step.title}</div>
                              <div className="text-xs text-muted-foreground mt-1">{step.time}</div>
                            </div>
                            <Badge 
                              variant={
                                step.status === 'completed' ? 'default' : 
                                step.status === 'in-progress' ? 'secondary' : 'outline'
                              }
                              className="text-xs"
                            >
                              {step.status}
                            </Badge>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Actions & Status */}
                <div className="space-y-6">
                  {/* Next Actions */}
                  <Card className="bg-white/5 border-white/20">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Zap className="h-5 w-5" />
                        Next Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {nextActions.map((action, index) => {
                        const Icon = action.icon;
                        return (
                          <div key={index} className="p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                <Icon className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm">{action.title}</h4>
                                <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                              </div>
                            </div>
                            <div className="mt-3">
                              <Button variant={action.variant} size="sm" className="w-full">
                                {action.title}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Setup Status */}
                  <Card className="bg-white/5 border-white/20">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Setup Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-4 rounded-lg border border-green-500/20 bg-green-500/10">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Onboarding Progress: Good</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          Most setup steps completed. Security configuration needs attention.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center p-3 rounded-lg border border-white/10 bg-white/5">
                          <div className="text-2xl font-bold text-green-600">75%</div>
                          <div className="text-xs text-muted-foreground">Complete</div>
                        </div>
                        <div className="text-center p-3 rounded-lg border border-white/10 bg-white/5">
                          <div className="text-2xl font-bold text-blue-600">2</div>
                          <div className="text-xs text-muted-foreground">Steps Left</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                <span className="text-green-600">Onboarding in progress</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Save Progress</Button>
                <Button>Complete Setup</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
